import {
    Container,
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem, Typography, Paper,
} from '@mui/material';
import Grid from '@mui/material/Grid2';

import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import React, { useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Edit, Delete } from '@mui/icons-material';

import axios from '@shared/config/axios';
import { ORDER_STATUSES_ARR } from '@shared/api/constants';
import { AxiosErrorAlertHandler, formatISOToCustom, isISO8601 } from '@shared/api/utils';
import { Alert } from '@features/alert';
import { TypographyOrderStatus } from '@features/typography-colored-status-order';
import { LoadingPage } from '@widgets/loading-page';


export const OrdersPage = () => {
    const alertRef = useRef(null);

    const queryClient = useQueryClient();
    const [paginationModel, setPaginationModel] = useState({
        pageSize: 15,
        page: 0,
    });

    const [selectedOrder, setSelectedOrder] = useState(null);
    const [editingRow, setEditingRow] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [open, setOpen] = useState(false);


    // Fetch bags with backend pagination
    const { data, isLoading } = useQuery({
        queryKey: ['orders-admin', paginationModel],
        queryFn: async () => {
            const response = await axios.get('/orders');
            return response.data;
        },
        keepPreviousData: true,
    });

    // Update bag mutation
    const updateMutation = useMutation({
        mutationFn: ({ orderId, updatedOrder }) => axios.patch(`/orders/${orderId}`, updatedOrder),
        onSuccess: () => {
            queryClient.invalidateQueries(['orders-admin']);
            setDialogOpen(false);
        },
        onError: (error) => {
            AxiosErrorAlertHandler(error, alertRef);
        }
    });

    // Delete bag mutation
    const deleteMutation = useMutation({
        mutationFn: (id) => axios.delete(`/orders/${id}`),
        onSuccess: () => queryClient.invalidateQueries(['orders-admin']),
        onError: (error) => {
            AxiosErrorAlertHandler(error, alertRef);
        }
    });

    const handleOpenDialog = async (params) => {
        const response = await axios.get(`/orders/${params.row._id}`);
        setSelectedOrder(response.data);
        setOpen(true);
    };

    const handleEdit = (row) => {
        setEditingRow(row);
        setDialogOpen(true);
    };

    const handleDelete = (id) => {
        deleteMutation.mutate(id);
    };

    const handleSave = () => {
        if (editingRow) {
            updateMutation.mutate({
                orderId: editingRow._id,
                updatedOrder: {
                    first_name: editingRow.first_name,
                    last_name: editingRow.last_name,
                    phone: editingRow.phone,
                    status: editingRow.status,
                }
            });
        }
    };

    const handleDialogClose = () => {
        setEditingRow(null);
        setDialogOpen(false);
    };

    const columns = [
        {
            field: 'actions',
            type: 'actions',
            headerName: '',
            width: 25,
            cellClassName: 'actions',
            hideSortIcons: true,
            getActions: ({ row }) => {
                return [
                    <GridActionsCellItem
                        icon={<Edit/>}
                        label="edit"
                        onClick={() => handleEdit(row)}
                        showInMenu
                    />,
                    <GridActionsCellItem
                        icon={<Delete/>}
                        label="delete"
                        onClick={() => handleDelete(row._id)}
                        showInMenu
                    />,
                ];
            }
        },
        { field: '_id', headerName: 'id', width: 70, valueGetter: (params) => params.slice(-5) },
        { field: 'uuid', headerName: 'uuid', width: 70, valueGetter: (params) => params.slice(-5) },
        {
            field: 'created_at',
            headerName: 'order received',
            width: 135,
            valueFormatter: (value) => {
                if (isISO8601(value)) {
                    return formatISOToCustom(value);
                }
                return value;
            }
        },
        { field: 'first_name', headerName: 'first name', width: 140 },
        { field: 'last_name', headerName: 'last name', width: 140 },
        { field: 'phone', headerName: 'phone', width: 140 },
        { field: 'total_price', type: 'number', headerName: 'total amount', width: 110 },
        {
            field: 'status',
            headerName: 'status',
            width: 90,
            renderCell: (params) => (
                <TypographyOrderStatus status={params.value} sx={{ height: '100%', alignContent: 'center' }}/>
            )
        },
    ];

    if (isLoading) return <LoadingPage/>;

    return (
        <>
            <Container
                maxWidth="false"
                sx={{
                    width: '100%',
                    padding: { xs: 1, sm: 2, md: 3 },
                    height: '80vh'
                }}
            >
                <Box
                    sx={{
                        height: '100%',
                        width: '100%',
                    }}
                >
                    <DataGrid
                        rows={data || []}
                        columns={columns}
                        loading={isLoading}
                        paginationModel={paginationModel}
                        onPaginationModelChange={setPaginationModel}
                        pageSizeOptions={[5, 10, 15]}
                        getRowId={(row) => row._id}
                        onRowClick={handleOpenDialog}
                    />
                </Box>
            </Container>

            {/* Packs Information*/}
            <Dialog open={open} onClose={() => setOpen(false)}>
                <DialogTitle>
                    <Typography variant="h5" gutterBottom sx={{ borderBottom: '2px solid #ccc', pb: 2 }}>
                        order items details
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    {selectedOrder && (
                        <Paper
                            sx={{
                                p: 3,
                                display: 'flex',
                                flexDirection: 'column',
                                height: 'auto',
                                backgroundColor: '#f9f9f9',
                                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                                borderRadius: 2
                            }}
                        >
                            {selectedOrder?.packs?.length > 0 && selectedOrder.packs.map((pack) => (
                                <Grid
                                    container
                                    key={pack._id}
                                    spacing={1}
                                    sx={{
                                        borderBottom: '1px solid #e0e0e0',
                                        mb: 2,
                                    }}
                                >
                                    <Grid size={{ xs: 6 }}>
                                        <Typography variant="body2">
                                            <strong>country:</strong> {pack.country.name}
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>producer:</strong> {pack.producerName}
                                        </Typography>
                                    </Grid>
                                    <Grid size={{ xs: 6 }}>
                                        <Typography variant="body2">
                                            <strong>grind:</strong> {pack.grind}
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>weight:</strong> {pack.weight} kg
                                        </Typography>
                                    </Grid>
                                </Grid>
                            ))}
                        </Paper>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)} color="secondary">
                        cancel
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Dialog */}
            <Dialog open={dialogOpen} onClose={handleDialogClose}>
                <DialogTitle>edit order details</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        margin="normal"
                        label="first name"
                        value={editingRow?.first_name || ''}
                        onChange={(e) => setEditingRow({ ...editingRow, first_name: e.target.value })}
                    />
                    <TextField
                        fullWidth
                        margin="normal"
                        label="last name"
                        value={editingRow?.last_name || ''}
                        onChange={(e) => setEditingRow({ ...editingRow, last_name: e.target.value })}
                    />
                    <TextField
                        fullWidth
                        margin="normal"
                        label="phone"
                        value={editingRow?.phone || ''}
                        onChange={(e) => setEditingRow({ ...editingRow, phone: e.target.value })}
                    />
                    <FormControl fullWidth margin="normal">
                        <InputLabel>status</InputLabel>
                        <Select
                            variant="outlined"
                            value={editingRow?.status || ''}
                            onChange={(e) => setEditingRow({ ...editingRow, status: e.target.value })}
                            label="status"
                        >
                            {ORDER_STATUSES_ARR.map((status) => (
                                <MenuItem key={status} value={status}>
                                    {status.charAt(0).toLowerCase() + status.slice(1).replace('_', ' ')}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose} color="secondary">
                        cancel
                    </Button>
                    <Button onClick={handleSave} color="primary">
                        save
                    </Button>
                </DialogActions>
            </Dialog>
            <Alert ref={alertRef}/>
        </>
    );
};
